import React from 'react';
import { Dialog, DialogContent, DialogTitle, Slide, Stack, Typography, Avatar, IconButton, Box, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ChatsCircle, Phone, VideoCamera, X } from 'phosphor-react';
import createAvatar from '../utils/createAvatar';
import { useSelector, useDispatch } from 'react-redux';
import { SetPagerModalOpen, SetConversationUser, SetChatParams, SetSelectedChatId } from '../redux/slices/app'; // Import SetSelectedChatId
import useSocket from '../hooks/useSocket';
import { useNavigate } from 'react-router-dom';
import { PATH_DASHBOARD } from '../routes/paths';
import { PROFILE_IMAGE_URL } from '../config'; // Import PROFILE_IMAGE_URL

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PagerReceivedModal = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { makeCall, BASE_URL, stopBeep } = useSocket(); // Import stopBeep from useSocket
  const { pagerModalOpen, currentPagerData } = useSelector((state) => state.app);

  const senderName = currentPagerData?.senderName || 'Unknown Sender';
  const senderDepartment = currentPagerData?.senderDepartment || 'N/A';
  const senderCallingId = currentPagerData?.senderId;
  const senderProfilePicture = currentPagerData?.profilePicture; // Assuming profilePicture might be in pagerData

  const avatarProps = createAvatar(senderName);
  const avatarSrc = senderProfilePicture 
    ? (senderProfilePicture.startsWith('http') 
        ? senderProfilePicture 
        : `${PROFILE_IMAGE_URL}${senderCallingId}`) // Use PROFILE_IMAGE_URL
    : undefined;

  const handleClose = () => {
    dispatch(SetPagerModalOpen(false));
    stopBeep(); // Stop the beep sound when the modal is closed
  };

  const handleInitiateCall = (type) => {
    if (senderCallingId && senderName) {
      makeCall(senderName, senderCallingId, type);
    } else {
      console.error("Cannot initiate call: senderCallingId or senderName is missing.");
    }
    handleClose(); // Close modal after initiating call
  };

  const handleMessageUser = () => {
    if (senderCallingId && senderName) {
      const storedUser = sessionStorage.getItem('currentUser');
      const parsedUser = storedUser ? JSON.parse(storedUser) : null;
      const currentUserCallingId = parsedUser?.callingId || parsedUser?.userId || parsedUser?.id;
      const currentUserName = `${parsedUser?.firstName || ''} ${parsedUser?.lastName || ''}`.trim() || 'You';
      
      // Construct canonical chat_id
      const sortedIds = [String(senderCallingId), String(currentUserCallingId)].sort();
      const chatId = `${sortedIds[0]}${sortedIds[1]}`;

      dispatch(SetSelectedChatId(chatId)); // Use canonical ID
      dispatch(SetConversationUser({
        name: senderName,
        senderId: senderCallingId,
        profilePicture: senderProfilePicture ? `${PROFILE_IMAGE_URL}${senderCallingId}` : null, // Use PROFILE_IMAGE_URL
      }));
      
      dispatch(SetChatParams({
        receiverId: senderCallingId,
        contact_name: senderName,
        senderId: currentUserCallingId,
        sender_name: currentUserName,
        chat_type: 'single',
      }));
      
      navigate(PATH_DASHBOARD.general.app);
    } else {
      console.error("Cannot message user: senderCallingId or senderName is missing.");
    }
    handleClose(); // Close modal after navigating to chat
  };

  return (
    <Dialog
      open={pagerModalOpen}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      aria-describedby="pager-received-modal-description"
      fullWidth
      maxWidth="xs"
      sx={{ '& .MuiDialog-paper': { borderRadius: 4 } }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1.5, pt: 1.6, position: 'relative' }}>
        <Typography variant="h5" sx={{ color: theme.palette.text.primary }}>
          New Pager Received
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (t) => t.palette.grey[500],
          }}
        >
          <X />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3} alignItems="center" justifyContent="center">
          <Avatar
            src={avatarSrc}
            alt={senderName}
            sx={{
              width: 96,
              height: 96,
              fontSize: '2.5rem',
              bgcolor: avatarProps.color,
              color: theme.palette.getContrastText(avatarProps.color),
            }}
          >
            {!avatarSrc && avatarProps.name}
          </Avatar>
          <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
            {senderName}
          </Typography>
          <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary }}>
            Department: {senderDepartment}
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.primary, textAlign: 'center' }}>
            Msg: Please Contact assp.
          </Typography>
          
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <IconButton
              onClick={handleMessageUser}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
                width: 56,
                height: 56,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              <ChatsCircle size={28} />
            </IconButton>
            <IconButton
              onClick={() => handleInitiateCall('audio')}
              sx={{
                backgroundColor: theme.palette.success.main,
                color: theme.palette.common.white,
                width: 56,
                height: 56,
                '&:hover': {
                  backgroundColor: theme.palette.success.dark,
                },
              }}
            >
              <Phone size={28} />
            </IconButton>
            <IconButton
              onClick={() => handleInitiateCall('video')}
              sx={{
                backgroundColor: theme.palette.info.main,
                color: theme.palette.common.white,
                width: 56,
                height: 56,
                '&:hover': {
                  backgroundColor: theme.palette.info.dark,
                },
              }}
            >
              <VideoCamera size={28} />
            </IconButton>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default PagerReceivedModal;